/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";
import { createCheckoutSession, getCoupons, ProfileDetails } from "../core/_requests";
import Header from "../../sidebar/Header";
import Footer from "../../sidebar/Footer";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

interface UserProfile {
  name: string;
  walletBalance: number;
  email?: string;
  profile?: string;
  contactNumber?: string;
}

interface FeatureDetail {
  id: number;
  name: string;
  description: string;
}

interface PlanDetail {
  amount: number;
  validity: number;
  validityCount: string;
  type: number; // 1=Yearly, 2=Monthly
  isActive: boolean;
}

interface Plan {
  id: string;
  name: string;
  description?: string;
  featuresDetails: FeatureDetail[];
  plan_details: PlanDetail[];
}

interface LocationState {
  plan: Plan;
  selectedDetail: PlanDetail;
  walletBalance: number;
  couponId?: string;
  userId?: string;
}

interface AppliedCoupon {
stripeCouponId: string;
  name: string;
  discount: number;
}

const SubscriptionDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
    const { id: userId } = useParams<{ id: any }>();
   
  // const { userId: paramUserId } = useParams<{ userId: string }>();
  const { plan, selectedDetail, walletBalance = 0, couponId } =
    (location.state || {}) as LocationState;

 
// const userId: string = paramUserId || locationUserId || storedUserId || user_id || "";

 
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [allCoupons, setAllCoupons] = useState<any[]>([]);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [walletBalances, setWalletBalances] = useState(0);
  const [subscribedActivePlan, setSubscribedActivePlan] = useState<Plan | any | null>(null);
  const [walletInputAmount, setWalletInputAmount] = useState(0);
  const [paymentType, setPaymentType] = useState(2); // 1=Wallet, 2=Card, 3=Partial

  const originalAmount = Number(selectedDetail?.amount) || 0;
  const couponDiscount = appliedCoupon ? appliedCoupon.discount : 0;
  const total = Math.max(originalAmount - couponDiscount, 0);

  // Fetch profile & wallet
  // inside SubscriptionDetailsPage

// Fetch profile & wallet
const fetchProfileDetails = async () => {
  try {
    
    const res = await ProfileDetails(userId);
    
    setUserProfile(res?.data);
    setWalletBalances(res.data?.walletBalance || 0);
  } catch (err) {
    console.error(" Profile fetch error:", err);
    setWalletBalances(0);
  }
};
useEffect(() => {
  const fetchCoupons = async () => {
    try {
      const res = await getCoupons({ couponType: "subscription" });
      
      setAllCoupons(res?.data || []);
    } catch (err) {
      console.error(" Error fetching coupons:", err);
      setAllCoupons([]);
    }
  };
  fetchCoupons();
}, []);

useEffect(() => {
  if (userId) {
    fetchProfileDetails();  
  } else {
    console.warn(" No userId found, skipping profile fetch");
  }
}, [userId]);
// Apply coupon by id
useEffect(() => {
  
  if (!selectedDetail || !allCoupons.length || !couponId) return;
  const coupon = allCoupons.find((c) => c.primaryId === couponId);
  
  if (coupon) {
    const discount =
      coupon.couponValueType === 2
        ? (selectedDetail.amount * coupon.amount) / 100
        : coupon.amount;
    
  setAppliedCoupon({
  stripeCouponId: coupon.stripeCouponId,
  name: coupon.name,
  discount,
});

  }
}, [couponId, allCoupons, selectedDetail]);

// Wallet + payment type logic
useEffect(() => {
  
  if (!selectedDetail) return;
  if (walletBalances > 0) {
    if (walletBalances >= total) {
      
      setWalletInputAmount(total);
      setPaymentType(1);
    } else {
      
      setWalletInputAmount(walletBalances);
      setPaymentType(3);
    }
  } else {
    
    setWalletInputAmount(0);
    setPaymentType(2);
  }
}, [walletBalances, total, selectedDetail]);

// Apply coupon manually
const handleApplyCoupon = () => {
  
  const coupon = allCoupons.find(
    (c) => c.name.toUpperCase() === couponCode.trim().toUpperCase()
  );
  console.log("COUPON OBJECT", coupon);
  if (!coupon) {
    console.warn("⚠️ Invalid coupon entered:", couponCode);
    toast.error("Invalid coupon code");
    setAppliedCoupon(null);
    return;
  }


  const discount =
    coupon.couponValueType === 1
      ? (originalAmount * coupon.amount) / 100
      : coupon.amount;

  

 setAppliedCoupon({
 stripeCouponId: coupon.stripeCouponId,
  name: coupon.name,
  discount,
});
console.log(" Applied coupon:", coupon.primaryId);
  const netAmount = Math.max(originalAmount - discount, 0);
  

  if (walletBalances > 0) {
    if (walletBalances >= netAmount) {
      
      setPaymentType(1);
      setWalletInputAmount(netAmount);
    } else {
    
      setPaymentType(3);
      setWalletInputAmount(walletBalances);
    }
  } else {
    
    setPaymentType(2);
    setWalletInputAmount(0);
  }
  toast.success(`Coupon ${coupon.name} applied!`);
};

// Create checkout session
// Create checkout session
const handleSubscribe = async (): Promise<string | void> => {
  const walletUsed =
    paymentType === 1 ? total : paymentType === 3 ? walletInputAmount : 0;

const subscriptionData: any = {
  planId: plan.id,
  originalAmount,
  amount: total,
  user_id: userId,
  type: selectedDetail.type,
  validity: selectedDetail.validity,
  paymentType,
  walletInputAmount: walletUsed,
};

if (appliedCoupon?.stripeCouponId) {
  subscriptionData.stripeCouponId = appliedCoupon.stripeCouponId;
}


console.log("SUBSCRIBE PAYLOAD", subscriptionData);

  

  try {
    setLoading(true);
    const sessionData = await createCheckoutSession(subscriptionData);
    

    if (sessionData.status) {
      localStorage.setItem("subscribedPlanId", plan.id);
      localStorage.setItem("userId", userId);

      //  Wallet-only → no Stripe
      if (paymentType === 1 && walletUsed >= total) {
        toast.success("Subscription successful using wallet!");
        setSubscribedActivePlan(plan);
        navigate(`/subscriptions/${userId}`);
        return;
      }

      //  Stripe flow
      toast.success("Redirecting to payment...");
      localStorage.setItem("sessionId", sessionData.id);
      if (sessionData.paymentIntentId) {
        localStorage.setItem("paymentIntentId", sessionData.paymentIntentId);
      }

      setSubscribedActivePlan(plan);
      return sessionData.id;
    } else {
      throw new Error(sessionData.message || "Failed to create checkout session");
    }
  } catch (error: any) {
    console.error(" Error in handleSubscribe:", error);
    toast.error(error.message || "Failed to process subscription");
    throw error;
  } finally {
    setLoading(false);
  }
};

// Handle Stripe payment
const handlePayment = async () => {
  
  try {
    const sessionId = await handleSubscribe();
    
    if (sessionId) {
      const stripe = await stripePromise;
      if (!stripe) {
        console.error(" Stripe failed to initialize");
        return toast.error("Stripe failed to initialize.");
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) console.error(" Error redirecting to payment:", error.message);
    }
  } catch (error) {
    console.error(" handlePayment error:", error);
  }
};

  if (!plan || !selectedDetail) {
    return (
      <div className="container mt-5 text-center">
        <h5>No plan selected.</h5>
        <button
          className="btn btn-outline-secondary mt-3"
          onClick={() => navigate("/subscription/list")}
        >
          Back to Plans
        </button>
      </div>
    );
  }

  const features = plan.featuresDetails || [];

  return (
    <div className="container my-5">
      <Header
        userProfile={userProfile}
        walletBalance={walletBalances}
        subscribedActivePlan={subscribedActivePlan}
      />
      <h2 className="mb-4 text-center fw-bold">Subscription Details</h2>
      <div className="row justify-content-center">
        {/* Package Info */}
        <div className="col-lg-5 col-md-7 mb-4">
          <div className="card p-4 shadow-sm h-100">
            <h5 className="mb-3 fw-semibold">
              Package: <span>{plan.name}</span>
              <span
                className="float-end text-danger"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/subscription/list")}
              >
                <i className="bi bi-trash"></i>
              </span>
            </h5>
            <hr />
            <div className="mb-3">
              <h6 className="fw-semibold">Plan Details:</h6>
              <p className="mb-1">Duration: {selectedDetail.validity}</p>
              <p className="mb-1">
                Type: {selectedDetail.type === 1 ? "Yearly" : "Monthly"}
              </p>
            </div>
            <div className="mb-3">
              <h6 className="fw-semibold">Features:</h6>
              <ul className="list-unstyled">
                {features.map((f) => (
                  <li key={f.id} className="d-flex align-items-center my-1">
                    <img
                      src="/assets/images/vector.png"
                      alt="tick"
                      className="me-2"
                      style={{ width: "15px", height: "15px" }}
                    />
                    <span>{f.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            <hr />
            <h5 className="fw-bold">Total Price: ${originalAmount.toFixed(2)}</h5>
            <button
              onClick={() => navigate(`/subscriptions/${userId}`)}
              className="btn btn-outline-secondary mt-3 w-100"
            >
              ← Back to Plans
            </button>
          </div>
        </div>

        {/* Payment & Coupon */}
        <div className="col-lg-4 col-md-5 mb-4">
          <div className="card p-4 shadow-sm h-100">
            <h5 className="mb-3 fw-semibold">Bill Details</h5>
            <table className="table table-borderless mb-3">
              <tbody>
                <tr>
                  <td>Original Price</td>
                  <td className="text-end">${originalAmount.toFixed(2)}</td>
                </tr>
                {couponDiscount > 0 && (
                  <tr>            
                    <td>Coupon Discount ({appliedCoupon?.name})</td>
                    <td className="text-end text-success">-${couponDiscount.toFixed(2)}</td>
                  </tr>
                )}
                <tr>
                  <td className="fw-bold">Final Amount</td>
                  <td className="text-end fw-bold">${total.toFixed(2)}</td>
                </tr>
                {walletBalances > 0 && (
                  <tr>
                    <td>Wallet Balance</td>
                    <td className="text-end">${walletBalances.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Payment Methods */}
            <div className="mb-3">
              <h6 className="fw-semibold mb-2">Payment Method</h6>
              {walletBalances > 0 && (
                <div className="mb-2">
                  <label className="form-check-label">
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      checked={paymentType === 1}
                      onChange={() => {
                        setPaymentType(1);
                        setWalletInputAmount(Math.min(total, walletBalances));
                      }}
                    />
                    Wallet Only (${walletBalances.toFixed(2)})
                  </label>
                </div>
              )}

              {walletBalances > 0 && walletBalances < total && (
                <div className="mb-2">
                  <label className="form-check-label">
                    <input
                      type="radio"
                      className="form-check-input me-2"
                      checked={paymentType === 3}
                      onChange={() => {
                        setPaymentType(3);
                        setWalletInputAmount(walletBalances);
                      }}
                    />
                    Partial Wallet + Card
                  </label>
                </div>
              )}

              <div>
                <label className="form-check-label">
                  <input
                    type="radio"
                    className="form-check-input me-2"
                    checked={paymentType === 2}
                    onChange={() => {
                      setPaymentType(2);
                      setWalletInputAmount(0);
                    }}
                  />
                  Card Only
                </label>
              </div>

              {paymentType === 3 && (
                <div className="mt-2">
                  <label>Wallet Amount to Use:</label>
                  <input
                    type="number"
                    min={0}
                    max={walletBalances}
                    value={walletInputAmount}
                    onChange={(e) =>
                      setWalletInputAmount(
                        Math.min(walletBalances, Math.max(0, Number(e.target.value)))
                      )
                    }
                    style={{ width: "80px", textAlign: "right", marginLeft: "10px" }}
                  />
                </div> 
              )}
            </div>

            {/* Coupon Section */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span>Have a coupon?</span>
                <span
                  style={{ color: "#185abc", cursor: "pointer" }}
                  onClick={() => setShowCouponModal(true)}
                >
                  View All
                </span>
              </div>
              <div className="input-group mt-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Apply Coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={handleApplyCoupon}
                  disabled={!couponCode.trim()}
                >
                  APPLY
                </button>
              </div>
              {appliedCoupon && (
                <p className="text-success mt-2">
                  Coupon Applied: -${couponDiscount.toFixed(2)}
                </p>
              )}
            </div>

            {/* Payment Button */}
            <button
              className="btn btn-primary w-100 fw-bold"
              style={{ background: "rgb(51, 115, 8)", border: "none", fontSize: "1.05em" }}
              onClick={handlePayment}
              disabled={loading}
            >
              {loading ? "PROCESSING..." : "PROCEED TO PAYMENT"}
            </button>
          </div>
        </div>
      </div>

      {/* Coupon Modal */}
      <Modal show={showCouponModal} onHide={() => setShowCouponModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Available Coupons</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {allCoupons.length === 0 && <p>No coupons available.</p>}
          {allCoupons.map((c) => {
            const isApplicable =
              c.planId === "00000000-0000-0000-0000-000000000000" || c.planId === plan.id;
            return (
              <div
                key={c.primaryId}
                className={`coupon-item mb-2 p-2 border rounded ${
                  !isApplicable ? "text-muted" : "cursor-pointer"
                }`}
                style={{ cursor: isApplicable ? "pointer" : "not-allowed" }}
                onClick={() => {
                  if (!isApplicable) return;
                  setCouponCode(c.name);
                  setShowCouponModal(false);
                }}
              >
                <strong>{c.name}</strong> –{" "}
                {c.couponValueType === 1 ? `${c.amount}% off` : `$${c.amount} off`}
              </div>
            );
          })}
        </Modal.Body>
      </Modal>

      <Footer />
    </div>
  );
};

export default SubscriptionDetailsPage;
